import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Eye, EyeOff, Shield, Smartphone, Key, RefreshCw, Mail, AlertTriangle } from 'lucide-react';
import { QRCodeCanvas as QRCode } from 'qrcode.react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: `${BACKEND_URL}/api` });

// Steps: 'password' | 'totp' | 'setup_totp' | 'recovery' | 'email_recovery' | 'emergency'
export default function AdminLoginPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState('password');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  // Password step
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [pendingToken, setPendingToken] = useState('');

  // TOTP step
  const [totpCode, setTotpCode] = useState('');

  // Setup step
  const [qrUri, setQrUri] = useState('');
  const [setupCode, setSetupCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [codesShown, setCodesShown] = useState(false);

  // Recovery step (backup code)
  const [recoveryCode, setRecoveryCode] = useState('');

  // Email recovery step
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [emailSent, setEmailSent] = useState(false);

  // Emergency reset step
  const [emergencyKey, setEmergencyKey] = useState('');
  const [emergencyEmail, setEmergencyEmail] = useState('');
  const [showEmergencyKey, setShowEmergencyKey] = useState(false);

  // Check portal status on mount — also handle ?recovery_token= in URL
  useEffect(() => {
    const token = localStorage.getItem('agripos_token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const isExpired = payload.exp && payload.exp < Date.now() / 1000;
        if (payload.is_super_admin && !isExpired) {
          window.location.href = '/superadmin';
          return;
        }
      } catch {}
    }
    // Auto-process recovery token from email link
    const params = new URLSearchParams(window.location.search);
    const rt = params.get('recovery_token');
    if (rt) {
      handleEmailRecoveryToken(rt);
    }
  }, []);

  const handleEmailRecoveryToken = async (token) => {
    setLoading(true);
    setError('');
    try {
      const res = await api.post('/admin-auth/email-recovery', { token });
      setPendingToken(res.data.pending_token);
      // Clear token from URL cleanly
      window.history.replaceState({}, '', '/admin');
      // Go straight to TOTP setup
      const setupRes = await api.post('/admin-auth/setup-totp', { pending_token: res.data.pending_token });
      setQrUri(setupRes.data.qr_uri);
      setPendingToken(setupRes.data.pending_token);
      setInfo('Email verified. Scan the QR code below to set up your new Google Authenticator.');
      setStep('setup_totp');
    } catch (err) {
      setError(err.response?.data?.detail || 'Recovery link is invalid or expired.');
      setStep('password');
    }
    setLoading(false);
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await api.post('/admin-auth/login', { email, password });
      setPendingToken(res.data.pending_token);
      if (res.data.totp_ready) {
        setStep('totp');
      } else {
        const setupRes = await api.post('/admin-auth/setup-totp', { pending_token: res.data.pending_token });
        setQrUri(setupRes.data.qr_uri);
        setPendingToken(setupRes.data.pending_token);
        setStep('setup_totp');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid credentials');
    }
    setLoading(false);
  };

  const handleTotpSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await api.post('/admin-auth/totp', { pending_token: pendingToken, code: totpCode });
      finishLogin(res.data.token, res.data.user);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid code');
      setTotpCode('');
    }
    setLoading(false);
  };

  const handleSetupVerify = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await api.post('/admin-auth/verify-setup', { pending_token: pendingToken, code: setupCode });
      if (!res.data.verified) {
        setError(res.data.error || 'Invalid code');
        setLoading(false);
        return;
      }
      setBackupCodes(res.data.backup_codes);
      setCodesShown(true);
      setPendingToken(res.data.token);
    } catch (err) {
      setError(err.response?.data?.detail || 'Verification failed');
    }
    setLoading(false);
  };

  const handleRecovery = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await api.post('/admin-auth/recover', { pending_token: pendingToken, recovery_code: recoveryCode });
      finishLogin(res.data.token, res.data.user);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid recovery code');
    }
    setLoading(false);
  };

  const handleRequestEmailRecovery = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await api.post('/admin-auth/request-email-recovery', { email: recoveryEmail });
      setEmailSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send recovery email');
    }
    setLoading(false);
  };

  const handleEmergencyReset = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await api.post('/admin-auth/emergency-reset', {
        emergency_key: emergencyKey,
        email: emergencyEmail,
      });
      setPendingToken(res.data.pending_token);
      const setupRes = await api.post('/admin-auth/setup-totp', { pending_token: res.data.pending_token });
      setQrUri(setupRes.data.qr_uri);
      setPendingToken(setupRes.data.pending_token);
      setInfo('Emergency reset successful. Scan the QR code to set up your new Google Authenticator.');
      setStep('setup_totp');
    } catch (err) {
      setError(err.response?.data?.detail || 'Emergency reset failed');
    }
    setLoading(false);
  };

  const finishLogin = (token, _user) => {
    localStorage.setItem('agripos_token', token);
    window.location.href = '/superadmin';
  };

  const afterCodesSaved = () => {
    localStorage.setItem('agripos_token', pendingToken);
    window.location.href = '/superadmin';
  };

  return (
    <div className="min-h-screen bg-[#060D1A] flex items-center justify-center p-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <div className="w-full max-w-sm">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-slate-800 border border-slate-700 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Shield size={26} className="text-emerald-400" />
          </div>
          <h1 className="text-white font-bold text-xl">Platform Administration</h1>
          <p className="text-slate-500 text-sm mt-1">Restricted access — authorized personnel only</p>
        </div>

        {/* Loading state (processing recovery token from URL) */}
        {loading && step === 'password' && (
          <div className="text-center py-8">
            <RefreshCw size={24} className="animate-spin text-emerald-400 mx-auto mb-3" />
            <p className="text-slate-400 text-sm">Processing recovery link…</p>
          </div>
        )}

        {/* Step: Password */}
        {step === 'password' && !loading && (
          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            {error && <div className="bg-red-500/10 border border-red-500/20 text-red-400 text-sm px-4 py-3 rounded-lg">{error}</div>}
            <div className="space-y-1.5">
              <Label className="text-slate-400 text-sm">Email</Label>
              <Input type="email" data-testid="admin-email" value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="admin@agribooks.com"
                className="bg-white/5 border-white/10 text-white placeholder:text-slate-600 h-11" autoFocus />
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-400 text-sm">Password</Label>
              <div className="relative">
                <Input type={showPw ? 'text' : 'password'} data-testid="admin-password"
                  value={password} onChange={e => setPassword(e.target.value)}
                  placeholder="Password"
                  className="bg-white/5 border-white/10 text-white placeholder:text-slate-600 h-11 pr-10" />
                <button type="button" onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
                  {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>
            <Button type="submit" disabled={loading} data-testid="admin-login-btn"
              className="w-full h-11 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold rounded-xl">
              {loading ? 'Verifying...' : 'Continue'}
            </Button>
          </form>
        )}

        {/* Step: TOTP */}
        {step === 'totp' && (
          <form onSubmit={handleTotpSubmit} className="space-y-4">
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 text-center mb-2">
              <Smartphone size={28} className="text-emerald-400 mx-auto mb-2" />
              <p className="text-white text-sm font-medium">Google Authenticator</p>
              <p className="text-slate-400 text-xs mt-1">Open your authenticator app and enter the 6-digit code for AgriBooks Platform Admin</p>
            </div>
            {error && <div className="bg-red-500/10 border border-red-500/20 text-red-400 text-sm px-4 py-3 rounded-lg">{error}</div>}
            <div className="space-y-1.5">
              <Label className="text-slate-400 text-sm">6-Digit Code</Label>
              <Input data-testid="admin-totp-code" value={totpCode}
                onChange={e => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000" maxLength={6}
                className="bg-white/5 border-white/10 text-white placeholder:text-slate-600 h-11 text-center text-xl tracking-[0.5em] font-mono"
                autoFocus />
            </div>
            <Button type="submit" disabled={loading || totpCode.length < 6} data-testid="admin-totp-btn"
              className="w-full h-11 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold rounded-xl">
              {loading ? 'Verifying...' : 'Verify & Sign In'}
            </Button>
            <div className="space-y-1 pt-1">
              <button type="button" onClick={() => { setStep('recovery'); setError(''); }}
                className="w-full text-center text-slate-500 text-xs hover:text-slate-300 transition-colors py-1">
                Lost access? Use a backup recovery code →
              </button>
              <button type="button" onClick={() => { setStep('email_recovery'); setError(''); setRecoveryEmail(email); }}
                className="w-full text-center text-slate-600 text-xs hover:text-slate-400 transition-colors py-1">
                No codes either? Send recovery link to my email →
              </button>
            </div>
          </form>
        )}

        {/* Step: First-time TOTP Setup */}
        {step === 'setup_totp' && !codesShown && (
          <form onSubmit={handleSetupVerify} className="space-y-4">
            {info && <div className="bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 text-xs px-4 py-3 rounded-lg">{info}</div>}
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-3 text-center">
              <p className="text-amber-300 text-xs font-medium">Scan QR code with Google Authenticator</p>
              <p className="text-amber-400/70 text-xs mt-0.5">Backup codes will be emailed to you</p>
            </div>
            {qrUri && (
              <div className="flex justify-center p-4 bg-white rounded-xl">
                <QRCode value={qrUri} size={180} />
              </div>
            )}
            {error && <div className="bg-red-500/10 border border-red-500/20 text-red-400 text-sm px-4 py-3 rounded-lg">{error}</div>}
            <div className="space-y-1.5">
              <Label className="text-slate-400 text-sm">Confirm Code</Label>
              <Input data-testid="admin-setup-code" value={setupCode}
                onChange={e => setSetupCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000" maxLength={6}
                className="bg-white/5 border-white/10 text-white h-11 text-center text-xl tracking-[0.5em] font-mono"
                autoFocus />
            </div>
            <Button type="submit" disabled={loading || setupCode.length < 6}
              className="w-full h-11 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold rounded-xl">
              {loading ? 'Setting up...' : 'Enable Authenticator'}
            </Button>
          </form>
        )}

        {/* Step: Show backup codes after setup */}
        {step === 'setup_totp' && codesShown && (
          <div className="space-y-4">
            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-3 text-center">
              <p className="text-emerald-300 text-sm font-semibold">Authenticator Enabled!</p>
              <p className="text-emerald-400/70 text-xs mt-0.5">Codes also sent to your email</p>
            </div>
            <div>
              <p className="text-slate-300 text-sm font-medium mb-1">Your 8 Recovery Backup Codes</p>
              <p className="text-slate-500 text-xs mb-3">Save these in a secure location. Each works once only.</p>
              <div className="grid grid-cols-2 gap-2">
                {backupCodes.map((code, i) => (
                  <div key={i} className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-xs text-emerald-300 text-center font-mono tracking-wider">
                    {code}
                  </div>
                ))}
              </div>
            </div>
            <Button onClick={afterCodesSaved}
              className="w-full h-11 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold rounded-xl">
              I've saved my codes — Enter Admin Panel →
            </Button>
          </div>
        )}

        {/* Step: Backup code recovery */}
        {step === 'recovery' && (
          <form onSubmit={handleRecovery} className="space-y-4">
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 text-center mb-2">
              <Key size={28} className="text-amber-400 mx-auto mb-2" />
              <p className="text-white text-sm font-medium">Use a Backup Code</p>
              <p className="text-slate-400 text-xs mt-1">Enter one of your 8 recovery codes (format: XXXXXX-XXXXXX)</p>
            </div>
            {error && <div className="bg-red-500/10 border border-red-500/20 text-red-400 text-sm px-4 py-3 rounded-lg">{error}</div>}
            <div className="space-y-1.5">
              <Label className="text-slate-400 text-sm">Backup Code</Label>
              <Input data-testid="admin-recovery-code" value={recoveryCode}
                onChange={e => setRecoveryCode(e.target.value.toUpperCase())}
                placeholder="XXXXXX-XXXXXX"
                className="bg-white/5 border-white/10 text-white placeholder:text-slate-600 h-11 text-center font-mono tracking-widest uppercase"
                autoFocus />
            </div>
            <Button type="submit" disabled={loading || !recoveryCode}
              className="w-full h-11 bg-amber-600 hover:bg-amber-500 text-white font-semibold rounded-xl">
              {loading ? 'Verifying...' : 'Use Backup Code'}
            </Button>
            <div className="space-y-1 pt-1">
              <button type="button" onClick={() => { setStep('totp'); setError(''); }}
                className="w-full text-center text-slate-500 text-xs hover:text-slate-300 flex items-center justify-center gap-1 py-1">
                <RefreshCw size={11} /> Back to authenticator code
              </button>
              <button type="button" onClick={() => { setStep('email_recovery'); setError(''); }}
                className="w-full text-center text-slate-600 text-xs hover:text-slate-400 py-1">
                No codes? Send recovery link to my email →
              </button>
            </div>
          </form>
        )}

        {/* Step: Email recovery request */}
        {step === 'email_recovery' && (
          <div className="space-y-4">
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 text-center">
              <Mail size={28} className="text-blue-400 mx-auto mb-2" />
              <p className="text-white text-sm font-medium">Email Recovery</p>
              <p className="text-slate-400 text-xs mt-1">We'll send a 15-minute recovery link to your registered admin email</p>
            </div>
            {error && <div className="bg-red-500/10 border border-red-500/20 text-red-400 text-sm px-4 py-3 rounded-lg">{error}</div>}
            {emailSent ? (
              <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4 text-center space-y-2">
                <p className="text-emerald-300 text-sm font-semibold">Check your inbox</p>
                <p className="text-emerald-400/70 text-xs">A recovery link was sent to <strong>{recoveryEmail}</strong>. It expires in 15 minutes. Check your spam folder if you don't see it.</p>
                <p className="text-slate-500 text-xs mt-2">Click the link in the email — it will bring you back here to set up your new authenticator.</p>
              </div>
            ) : (
              <form onSubmit={handleRequestEmailRecovery} className="space-y-3">
                <div className="space-y-1.5">
                  <Label className="text-slate-400 text-sm">Your Admin Email</Label>
                  <Input type="email" value={recoveryEmail}
                    onChange={e => setRecoveryEmail(e.target.value)}
                    placeholder="admin@agribooks.com"
                    className="bg-white/5 border-white/10 text-white placeholder:text-slate-600 h-11"
                    autoFocus />
                </div>
                <Button type="submit" disabled={loading || !recoveryEmail}
                  className="w-full h-11 bg-blue-600 hover:bg-blue-500 text-white font-semibold rounded-xl">
                  {loading ? 'Sending...' : 'Send Recovery Link'}
                </Button>
              </form>
            )}
            <div className="space-y-1 pt-1">
              <button type="button" onClick={() => { setStep('totp'); setError(''); setEmailSent(false); }}
                className="w-full text-center text-slate-500 text-xs hover:text-slate-300 flex items-center justify-center gap-1 py-1">
                <RefreshCw size={11} /> Back to login
              </button>
              <button type="button" onClick={() => { setStep('emergency'); setError(''); }}
                className="w-full text-center text-slate-700 text-xs hover:text-slate-500 py-1">
                No email access? Emergency server key →
              </button>
            </div>
          </div>
        )}

        {/* Step: Emergency reset via server key */}
        {step === 'emergency' && (
          <form onSubmit={handleEmergencyReset} className="space-y-4">
            <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 text-center">
              <AlertTriangle size={28} className="text-red-400 mx-auto mb-2" />
              <p className="text-white text-sm font-medium">Emergency Access</p>
              <p className="text-slate-400 text-xs mt-1">Use the secret key stored in your server's .env file</p>
            </div>
            {error && <div className="bg-red-500/10 border border-red-500/20 text-red-400 text-sm px-4 py-3 rounded-lg">{error}</div>}
            <div className="space-y-1.5">
              <Label className="text-slate-400 text-sm">Admin Email</Label>
              <Input type="email" value={emergencyEmail}
                onChange={e => setEmergencyEmail(e.target.value)}
                placeholder="admin@agribooks.com"
                className="bg-white/5 border-white/10 text-white placeholder:text-slate-600 h-11" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-400 text-sm">Emergency Key <span className="text-slate-600 font-normal">(from server .env)</span></Label>
              <div className="relative">
                <Input type={showEmergencyKey ? 'text' : 'password'}
                  value={emergencyKey}
                  onChange={e => setEmergencyKey(e.target.value)}
                  placeholder="EMERGENCY_RESET_KEY value"
                  className="bg-white/5 border-white/10 text-white placeholder:text-slate-600 h-11 pr-10 font-mono text-xs" />
                <button type="button" onClick={() => setShowEmergencyKey(!showEmergencyKey)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
                  {showEmergencyKey ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>
            <Button type="submit" disabled={loading || !emergencyKey || !emergencyEmail}
              className="w-full h-11 bg-red-700 hover:bg-red-600 text-white font-semibold rounded-xl">
              {loading ? 'Processing...' : 'Reset & Re-enroll'}
            </Button>
            <button type="button" onClick={() => { setStep('email_recovery'); setError(''); }}
              className="w-full text-center text-slate-600 text-xs hover:text-slate-400 flex items-center justify-center gap-1 py-1">
              <RefreshCw size={11} /> Back to email recovery
            </button>
          </form>
        )}

        <p className="text-center text-slate-700 text-xs mt-8">
          This portal is for authorized administrators only.
        </p>
      </div>
    </div>
  );
}
